/* Shared admin form atoms */
import { useState } from "react";

export const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-2">
      {label}
    </span>
    {children}
    {hint ? <span className="block mt-1 text-[0.7rem] text-neutral-500">{hint}</span> : null}
  </label>
);

export const TextInput = ({ value, onChange, ...rest }) => (
  <input
    type="text"
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors"
    {...rest}
  />
);

export const TextArea = ({ value, onChange, rows = 4, ...rest }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="w-full bg-[#0B0F14] border border-white/[0.10] rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#F4D64E]/60 transition-colors leading-relaxed"
    {...rest}
  />
);

export const Toggle = ({ value, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    data-testid="admin-toggle"
    className={`inline-flex items-center gap-3 px-3 py-2 rounded-md border transition-colors ${
      value
        ? "bg-[#F4D64E]/10 border-[#F4D64E]/40 text-[#F4D64E]"
        : "bg-[#0B0F14] border-white/[0.10] text-neutral-500"
    }`}
  >
    <span
      className={`w-3 h-3 rounded-full ${value ? "bg-[#F4D64E]" : "bg-neutral-600"}`}
    />
    <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase">{label}</span>
  </button>
);

export const Btn = ({ children, variant = "primary", ...rest }) => {
  const cls =
    variant === "primary"
      ? "text-neutral-950 bg-[#F4D64E] hover:bg-white"
      : variant === "ghost"
      ? "text-neutral-400 hover:text-white border border-white/[0.10]"
      : variant === "danger"
      ? "text-red-300 hover:text-white border border-red-400/30 hover:border-red-400/60 bg-red-500/[0.04]"
      : "text-[#F4D64E] border border-[#F4D64E]/40 hover:bg-[#F4D64E]/10";
  return (
    <button
      type="button"
      {...rest}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-[0.72rem] font-bold tracking-[0.18em] uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${cls} ${rest.className || ""}`}
    >
      {children}
    </button>
  );
};

export const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, danger = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-6">
      <div className="bg-[#0F141A] border border-white/[0.10] rounded-xl p-7 max-w-sm w-full">
        <h3 className="font-display text-xl font-black">{title}</h3>
        <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{message}</p>
        <div className="mt-7 flex justify-end gap-3">
          <Btn variant="ghost" onClick={onCancel} data-testid="confirm-cancel">Cancel</Btn>
          <Btn
            variant={danger ? "danger" : "primary"}
            onClick={onConfirm}
            data-testid="confirm-ok"
          >
            Confirm
          </Btn>
        </div>
      </div>
    </div>
  );
};

/* Tiny hook for save state */
export const useSaveState = () => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const wrap = async (fn) => {
    setError("");
    setSaving(true);
    setSaved(false);
    try {
      const res = await fn();
      setSaved(true);
      setTimeout(() => setSaved(false), 3500);
      return res;
    } catch (e) {
      setError(e.message || "Save failed");
      throw e;
    } finally {
      setSaving(false);
    }
  };
  return { saving, saved, error, wrap };
};
